export enum ActivityEventType {
  RoleActivation = "role_activation",
  Member = "member",
  Program = "program",
  Finance = "finance",
  User = "user",
  Configuration = "configuration",
  System = "system",
}
export enum ActivitySeverity {
  Info = "info",
  Warning = "warning",
  Critical = "critical",
}
export type ActivityActor = {
  id?: string
  name: string
  email?: string
  role?: string
}

export enum ActivityTargetEntityType {
  Member = "member",
  Program = "program",
  FinanceTransaction = "finance_transaction",
  User = "user",
  Branch = "branch",
  Group = "group",
  Ministry = "ministry",
  OfferingType = "offering_type",
  Role = "role",
  Other = "other",
}
export type ActivityTarget = {
  entityType: ActivityTargetEntityType
  entityId: string
  label: string
}

export type ActivityEvent = {
  id: string
  type: ActivityEventType
  action: string
  title: string
  summary: string
  actor: ActivityActor
  target?: ActivityTarget
  severity: ActivitySeverity
  occurredAt: string
  metadata?: Record<string, unknown>
  correlationId?: string
}

export type ActivityFilters = {
  q?: string
  types?: ActivityEventType[]
  actors?: string[]
  severity?: ActivitySeverity[]
  dateFrom?: string
  dateTo?: string
  pageIndex: number
  pageSize: number
  sortBy: ActivitySortBy
  sortDir: ActivitySortDir
}

export enum ActivitySortBy {
  OccurredAt = "occurredAt",
}
export enum ActivitySortDir {
  Asc = "asc",
  Desc = "desc",
}
export type ActivityListResult = {
  items: ActivityEvent[]
  total: number
  pageIndex: number
  pageSize: number
}

export {
  ACTIVITY_LOG_DOMAINS,
  ACTIVITY_LOG_DOMAIN_ENTITY_TYPES,
  ActivityLogActionType,
  ActivityLogDomain,
  ActivityLogEntityType,
  activityLogDomainOf,
} from "@ibada/shared-types/activity-logs"
export type { ActivityLogChange, ActivityLogChangeValue } from "@ibada/shared-types/activity-logs"

import {
  ActivityLogActionType,
  ActivityLogDomain,
  ActivityLogEntityType,
  type ActivityLogChange,
} from "@ibada/shared-types/activity-logs"

export const ACTIVITY_LOG_ENTITY_TYPES: ActivityLogEntityType[] = [
  ActivityLogEntityType.Branch,
  ActivityLogEntityType.Group,
  ActivityLogEntityType.Ministry,
  ActivityLogEntityType.IncomeCategory,
  ActivityLogEntityType.ExpenseCategory,
  ActivityLogEntityType.Member,
  ActivityLogEntityType.MemberRelationship,
  ActivityLogEntityType.FinanceTransaction,
  ActivityLogEntityType.CustomField,
  ActivityLogEntityType.User,
  ActivityLogEntityType.WorkspaceInvitation,
  ActivityLogEntityType.Budget,
  ActivityLogEntityType.BudgetLine,
]

export const ACTIVITY_LOG_ACTION_TYPES: ActivityLogActionType[] = [
  ActivityLogActionType.Created,
  ActivityLogActionType.Updated,
  ActivityLogActionType.Deleted,
  ActivityLogActionType.Enabled,
  ActivityLogActionType.Disabled,
  ActivityLogActionType.StatusChanged,
  ActivityLogActionType.Invited,
  ActivityLogActionType.AcceptedInvite,
  ActivityLogActionType.DeclinedInvite,
]

export type GetActivityLogsQuery = {
  branch?: "all" | string
  actorUserId?: string
  entityType?: ActivityLogEntityType
  actionType?: ActivityLogActionType
  domain?: ActivityLogDomain
  entityId?: string
  from?: string
  to?: string
  q?: string
  page?: number
  limit?: number
  sortBy?: "createdAt"
  sortOrder?: "asc" | "desc"
  filters?: string
}

export type ActivityLogItem = {
  id: string
  workspaceId: string
  branchId: string | null
  actorUserId: string | null
  actorDisplayName: string | null
  actorAvatarUrl: string | null
  actionType: ActivityLogActionType
  entityType: ActivityLogEntityType
  entityId: string
  entityLabel: string | null
  messageKey: string
  messageParams: Record<string, unknown> | null
  summary: string | null
  /** What the event changed, field by field; empty when the event was stored without a before / after. */
  changes: ActivityLogChange[]
  createdAt: string
  canPreview: boolean
}

export type GetActivityLogsResponse = {
  items: ActivityLogItem[]
  total: number
  page: number
  limit: number
}

/** Counts for the audit log's domain pills and person select (`GET /activity-logs/facets`). */
export type ActivityLogFacets = {
  total: number
  domains: Array<{ domain: ActivityLogDomain; count: number }>
  actors: Array<{ actorUserId: string; actorDisplayName: string | null; actorAvatarUrl: string | null; count: number }>
}
